import { useEffect } from "react";

import { CartService } from "@/components/Cart/CartService";
import { TBillingInterval } from "@/types/app";
import { TCheckoutType } from "../types.checkout";
import { useCheckoutSummaryQuery } from "./queries/useCheckoutSummaryQuery";

interface UseCartSummaryOptions {
  checkoutType: TCheckoutType;
  billingPeriod: TBillingInterval;
  enabled?: boolean;
}

/**
 * Watches CartService cart changes and fetches the order summary.
 * Delegates to useCheckoutSummaryQuery for caching — identical cart/codes
 * within 30s skip the network call. Syncs results back into CartService
 * so existing subscribers continue to work.
 */
export function useCartSummary({
  checkoutType,
  billingPeriod,
  enabled = true,
}: UseCartSummaryOptions) {
  const { summary, isLoading, isError } = useCheckoutSummaryQuery({
    checkoutType,
    billingPeriod,
    enabled,
  });

  useEffect(() => {
    if (isLoading) {
      CartService.setSummaryLoading();
      return;
    }
    if (isError) {
      CartService.setSummaryError();
      return;
    }
    if (summary) CartService.setSummaryData(summary);
  }, [isLoading, isError, summary]);
}
